import { MetaReaction, ReactionType } from "types/conversationInbox";
import { ReactionsContext } from "../message/types";
import noop from "lodash/noop";
import partition from "lodash/partition";
import { cn } from "@/lib/utils";
import "./message-reactions.css";

interface Props {
  isLeft: boolean;
  reactions: ReactionsContext;
  messageId: string;
  onReactionsChange: (data: ReactionsChangeData) => void;
}

export type ReactionsChangeData = AddReactionsChange | RemoveReactionsChange;
export interface AddReactionsChange {
  type: "add";
  messageId: string;
  reaction: MetaReaction;
}
export interface RemoveReactionsChange {
  type: "remove";
  messageId: string;
  reactionId: string;
}

export const MessageReaction = ({
  isLeft,
  reactions,
  messageId,
  onReactionsChange,
}: Props): JSX.Element => {
  const { skedReaction, smuReaction } = extractReactionSide(reactions);
  const onAddReaction = () => {
    onReactionsChange({
      type: "add",
      messageId,
      reaction: MetaReaction.Love,
    });
  };

  const onRemoveReaction = (reactionId: string) => {
    onReactionsChange({
      type: "remove",
      messageId,
      reactionId: reactionId,
    });
  };
  if (smuReaction) {
    if (skedReaction && isSameReaction(skedReaction, smuReaction)) {
      return (
        <EmojiReaction
          active={true}
          reaction={smuReaction}
          readonly={false}
          onIconClick={() => onRemoveReaction(skedReaction.id)}
          count={2}
        />
      );
    }
    const smuReactionJsx = (
      <EmojiReaction
        rightGap={isLeft}
        leftGap={!isLeft}
        active={true}
        reaction={smuReaction}
        readonly={true}
        onIconClick={noop}
      />
    );
    const skedReactionJsx = (
      <EmojiReaction
        active={!!skedReaction}
        reaction={skedReaction || { reaction: MetaReaction.Love }}
        readonly={false}
        rightGap={isLeft}
        leftGap={!isLeft}
        onIconClick={() => {
          if (skedReaction) {
            onRemoveReaction(skedReaction.id);
          } else {
            onAddReaction();
          }
        }}
      />
    );
    return isLeft ? (
      <div>
        {smuReactionJsx}
        {skedReactionJsx}
      </div>
    ) : (
      <div>
        {skedReactionJsx}
        {smuReactionJsx}
      </div>
    );
  }

  if (skedReaction) {
    return (
      <EmojiReaction
        active={true}
        reaction={skedReaction}
        readonly={false}
        onIconClick={() => onRemoveReaction(skedReaction.id)}
      />
    );
  }

  return (
    <EmojiReaction
      active={false}
      reaction={{ reaction: MetaReaction.Love }}
      readonly={false}
      onIconClick={() => onAddReaction()}
    />
  );
};

function isSameReaction(left: ReactionType, right: ReactionType) {
  return extractReactionText(left) === extractReactionText(right);
}

function extractReactionText(reaction: ReactionType) {
  return reaction.reaction === MetaReaction.Love
    ? "#love"
    : reaction.emoji || "#love";
}

function extractReactionSide(reactions: ReactionsContext) {
  const [skedReaction, smuReaction] = partition(
    reactions.reactions,
    (c) => c.reactee === reactions.skedAccountId
  );
  return {
    smuReaction: smuReaction[0] as ReactionType | undefined,
    skedReaction: skedReaction[0] as ReactionType | undefined,
  };
}

interface EmojiReactionProps {
  reaction: Pick<ReactionType, "reaction" | "emoji">;
  active: boolean;
  readonly: boolean;
  rightGap?: boolean;
  leftGap?: boolean;
  count?: number;
  onIconClick: (() => void) | undefined;
}
const EmojiReaction = ({
  active,
  reaction,
  readonly,
  rightGap,
  leftGap,
  count,
  onIconClick,
}: EmojiReactionProps) => {
  const expand = !!count && count > 1;
  const containerClassName = cn(
    "inline-block bg-inbox-border h-6 rounded-[20px] text-center align-text-bottom p-0.5 transition-[background-color] duration-150 ease-out",
    expand ? "w-10" : "w-6",
    readonly ? "cursor-default" : "cursor-pointer",
    leftGap ? "ml-[5px]" : "ml-[15px]",
    rightGap ? "mr-[5px]" : "mr-[15px]"
  );

  if (reaction.reaction === MetaReaction.Love || !reaction.emoji) {
    const heartActive = !readonly && active;
    return (
      <span
        data-testid="reaction-icon-container"
        onClick={readonly ? undefined : onIconClick}
        className={cn(
          containerClassName,
          heartActive && "bg-signal-negative-soft",
          heartActive && "inbox-heart-active"
        )}
      >
        <span
          className={cn(
            "align-middle",
            active ? "text-signal-negative" : "text-ink-muted"
          )}
        >
          <i
            className={cn(
              "fa-solid fa-heart inbox-heart-icon",
              active && "active"
            )}
          />
        </span>

        {expand && (
          <span className="text-xs pl-[5px] inline-block align-middle">
            {count}
          </span>
        )}
      </span>
    );
  }
  return (
    <span
      data-testid="reaction-icon-container"
      onClick={readonly ? undefined : onIconClick}
      className={containerClassName}
    >
      <span>{reaction.emoji}</span>
      {expand && (
        <span className="text-xs pl-[5px] inline-block align-[1px]">
          {count}
        </span>
      )}
    </span>
  );
};
